import { useMutation, useQuery } from "@tanstack/react-query";
import * as React from "react";
import { Checkbox, TextField } from "@mui/material";
import dayjs from "dayjs";
import { useStoreDetail } from "../../../lib/apis/hooks/store.hook";
import { useQuotationCopyList } from "../../../lib/apis/hooks/quotation.hook";
import { postCopyQuotation } from "../../../lib/apis/v1/store.service";
import FailSnackbar from "../../ui/snackbar/fail.component";
import SuccessSnackbar from "../../ui/snackbar/success.component";
import { getQueryClient } from "../../../config/queryClient.config";
import ConfirmModal from "../confirm.modal";
import Button from "../../ui/button";
import type { ModalComponentProps } from "../../../types/modal.types";
import type { quotationCopyForm, quotationCopyTarget } from "../../../lib/apis/dtos/request/quotation.request";

interface ModalProps extends ModalComponentProps {
  storeId?: string;
  /** 시세표 목록(ListQuotationItem)의 ownerId — 없으면 "" 로 보낸다 */
  ownerId?: string;
}

const VARIATIONS = [
  {
    label: "주요폰",
    value: "mainEditValue"
  },
  {
    label: "중저가폰",
    value: "lowEditValue"
  },
  {
    label: "키즈폰",
    value: "kidsEditValue"
  },
]

export default function QuotationPasteModal({ id, onClose, storeId = "", ownerId = "" }: ModalProps) {

  const [copyForm, setCopyForm] = React.useState<quotationCopyForm>({
    storeFromId: storeId,
    storeCopyTo: [],
    date: dayjs().format('YYYY-MM-DD'),
    mainEditValue: "",
    lowEditValue: "",
    kidsEditValue: ""
  });
  const { data } = useQuery({ ...useStoreDetail(storeId) });
  const { result } = data || {};

  // 복사 대상 목록은 업체 리스트에서 넘어온 ownerId 기준으로 조회 (없으면 "" 로 호출)
  const form = { page: 0, size: 50, ownerId, category: "ALL" };

  const { data: quotionCopyList } = useQuery({ ...useQuotationCopyList(form) });
  const copyList = quotionCopyList?.result || [];
  const filteredCopyList = (copyList || []).filter(props => copyForm.storeFromId != props?.storeId);

  // 부가서비스 복사는 기본 체크 — 제외할 지점만 여기에 담는다
  const [serviceExcludedIds, setServiceExcludedIds] = React.useState<number[]>([]);

  const selectedStoreIds = copyForm.storeCopyTo.map((item) => item.storeToId);
  const isStoreSelected = (id: number) => selectedStoreIds.includes(id);
  const isServiceCopy = (id: number) => !serviceExcludedIds.includes(id);

  const toTarget = (id: number): quotationCopyTarget => ({ storeToId: id, copyService: isServiceCopy(id) });

  const toggleStore = (id: number, checked: boolean) => {
    setCopyForm((prev) => ({
      ...prev,
      storeCopyTo: checked
        ? [...prev.storeCopyTo.filter((item) => item.storeToId != id), toTarget(id)]
        : prev.storeCopyTo.filter((item) => item.storeToId != id)
    }));
  };

  const toggleService = (id: number, checked: boolean) => {
    setServiceExcludedIds((prev) => (checked ? prev.filter((item) => item != id) : [...prev, id]));
    setCopyForm((prev) => ({
      ...prev,
      storeCopyTo: prev.storeCopyTo.map((item) => (item.storeToId == id ? { ...item, copyService: checked } : item))
    }));
  };


  const copyMutation = useMutation({
    mutationFn: (form: quotationCopyForm) => postCopyQuotation(form),
    onSuccess: (res) => {
      const { error } = res || {};

      if (error) {
        FailSnackbar("시세표 제출 실패")
      } else {
        SuccessSnackbar(`시세표 복사 완료 (대상 지점 ${copyForm.storeCopyTo.length}곳)`)
        getQueryClient().invalidateQueries({ queryKey: ["quotation-list"] })
        onClose?.();
      }
    }
  })

  const inputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/,/g, "");

    if (/^\d/.test(val)) {
      val = '+' + val;
    }

    // 2. 부호(+/-), 숫자, 소수점 한 자리까지 허용하는 정규식
    const regex = /^[-+]?\d*(\.\d{0,1})?$/;

    if (regex.test(val)) {
      // 3. 문자열 상태 그대로 저장 (부호 유지를 위해)
      setCopyForm((prev) => ({ ...prev, [e.target.id]: val }));
    }
  };



  return (
    <ConfirmModal
      className="max-h-[666px] w-[1200px]"
      title="시세표 복사하기"
      rightButton={{
        title: "시세표 복사하기",
        disabled: copyForm.storeCopyTo.length == 0,
        onClick: () => copyMutation.mutate(copyForm)
      }}
      id={id}
      onClose={onClose}
    >
      <h2>시세표 복사할 기준기점을 확인하고 복사될 대상 지점을 선택해주세요.</h2>
      <div className="mt-[20px] max-h-[478px] overflow-y-auto">
        <table className="w-full">
          <tr>
            <td className="bg-[#FAFAFA] px-6 py-[19px]" rowSpan={2}>복사 기준 지점</td>
            <td className="px-[24px] py-[11px]">{result?.storeName}</td>
            <td className="px-[24px] py-[11px]">
              <Button
                variant={"green"}
                label={"제출 완료"}
                className="h-6 px-[10px] rounded-[100px] text-[13px] font-normal"
              />
            </td>
            <td className="px-[24px] py-[11px]" colSpan={2}>{result?.storeAddress}</td>
          </tr>
          <tr>
            <td colSpan={4} className="px-6 py-4">
              <div className="w-full rounded-[8px] bg-gray-05 px-5 py-4 space-y-3">
                <p className="text-style-compact-04 text-primary-50 font-normal">복사 기준 지점 가격 기준으로 <span className="font-semibold">일괄 휴대폰 가격 +</span>/-하여 복사하기</p>
                <div className="flex-row-between">
                  {VARIATIONS.map(({ label, value }, index) => {
                    return (
                      <>
                        <div className="flex-row text-style-compact-05 gap-2" key={index}>
                          <p>{label} 전체 기준</p>
                          <TextField
                            className="w-[105px]"
                            id={value}
                            inputProps={{ maxLength: 5 }}
                            value={copyForm?.[value as keyof typeof copyForm]}
                            onChange={inputChange}
                            slotProps={{
                              input: {
                                placeholder: "만원 단위",
                                sx: {
                                  widows: "105px",
                                  height: "40px",
                                  backgroundColor: "#fff"
                                }
                              }
                            }}
                          />
                          <p className="font-normal">만원</p>
                        </div>
                        <div className="h-10 w-px bg-gray-20 last:hidden" />
                      </>
                    )
                  })}
                </div>
                <p className="text-style-compact-04 text-[#5A5A5A] font-normal whitespace-pre-line">
                  {`*사용하지 않으시려면 공란으로 두세요.
                  *’만원' 단위로 입력해주세요. 소수점 한자리까지(천원 단위까지) 표기 가능합니다.
                  *예시) 복사 기준 지점 기준, 주요폰인 A폰이 15만원이고 ‘주요폰 전체 기준’에 “+5”만원 입력한다면, 복사된 지점엔 20만원으로 복사됩니다.`}
                </p>
              </div>
            </td>
          </tr>

          <tr>
            <td className="bg-[#FAFAFA] px-6 py-[19px] whitespace-nowrap" rowSpan={filteredCopyList.length + 1}>
              복사 대상 지점
            </td>
            <td className="px-[16px] py-[5px]">
              <Checkbox
                checked={copyForm.storeCopyTo.length === filteredCopyList.length}
                onClick={(e) => {
                  const checked = (e.target as HTMLInputElement).checked;
                  if (checked) {
                    const allTargets = filteredCopyList
                      .filter((item) => item.isActive)
                      .map((item) => toTarget(Number(item.storeId)));
                    return setCopyForm((prev) => ({ ...prev, storeCopyTo: allTargets }));
                  } else {
                    return setCopyForm((prev) => ({ ...prev, storeCopyTo: [] }));
                  }
                }}
              />
              전체 선택
            </td>
            <td className="px-[16px] py-[5px] text-center whitespace-nowrap">부가서비스 복사</td>
            <td className="px-[16px] py-[5px]"></td>
            <td className="px-[16px] py-[5px]"></td>
          </tr>

          {filteredCopyList.map((props, index) => {
            const submitText =
              props.status == "SUBMITTED" ? "제출 완료" : "제출 미완료";
            return (
              <tr className="border" key={index}>
                {/* {index === 0 && (
                  <td className="bg-[#FAFAFA] px-6 py-[19px] whitespace-nowrap" rowSpan={filteredCopyList.length}>
                    복사 대상 지점
                  </td>
                )} */}
                <td className="px-[16px] py-[11px]">
                  <Checkbox
                    checked={isStoreSelected(Number(props.storeId))}
                    disabled={!props.isActive}
                    onClick={(e) => toggleStore(Number(props.storeId), (e.target as HTMLInputElement).checked)}
                  />
                  <span style={{ opacity: props.isActive ? 1 : 0.4 }}>{props?.storeName || ""}</span>
                </td>
                <td className="px-[16px] py-[11px] text-center">
                  <Checkbox
                    checked={isServiceCopy(Number(props.storeId))}
                    disabled={!props.isActive}
                    onClick={(e) => toggleService(Number(props.storeId), (e.target as HTMLInputElement).checked)}
                  />
                </td>
                <td className="px-[16px] py-[11px]">
                  {props.isActive ? (
                    <Button
                      variant={
                        submitText == "제출 완료" ? "primary" : "danger"
                      }
                      label={submitText}
                      className="h-6 px-[10px] rounded-[100px] text-[13px] font-normal"
                    />) : (
                    <span className="text-black/40 text-[14px]">비활성화 지점</span>)}

                </td>
                <td className="px-[16px] py-[11px]">{props?.storeAddress || ""}</td>
              </tr>
            );
          })}
        </table>
      </div>
    </ConfirmModal>
  );
}